import { memo, useMemo } from 'react';
import snakeCase from 'lodash.snakecase';
import styled from '@emotion/styled';

import { cleanUserInput } from '../../lib/cleanUserInput';
import { quillAlignment } from '../../lib/quillAlignment';
import { themeFont } from '../../lib/themeFont';
import FixedNextImage from '../NextImage/FixedNextImage';
import { useCurrentRoute } from '../../lib/useCurrentRoute';
import useTracking from '../../lib/useTracking';
import type {
  Wws_DressCodeItem,
  Wws_DressCodeArticle,
} from '../../types/graphql';

export const Item = styled('div')`
  max-width: 80%;
  margin: 0 auto;
  padding: 40px 20px;
`;

export const SectionHeading = styled('h2')`
  margin: 0 0 16px 0;

  ${themeFont('s4')}
  font-weight: 400;
`;

export const Description = styled('div')`
  margin-bottom: 40px;

  ${themeFont('s6')}

  ${quillAlignment}
`;

export const ArticlesSection = styled('section')`
  margin: 48px 0;
`;

export const ArticlesGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const ArticleCard = styled('a')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ArticleTitle = styled('div')`
  overflow: hidden;
  text-align: center;
  padding-top: 16px;

  ${themeFont('s6')}

  font-weight: 400;

  @media (max-width: 768px) {
    max-width: 100%;
    min-height: 80px;
  }
`;

export const AffiliatesSection = styled('section')`
  margin: 60px 0 40px 0;
`;

export const AffiliatesContainer = styled('div')`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 32px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const AffiliatesContent = styled('div')`
  ${themeFont('s6')}
`;

export const IntroBold = styled('span')`
  font-weight: 700;
`;

export const AffiliateLink = styled('a')`
  ${themeFont('s6')}
  font-weight: 700;
  text-decoration: underline;
`;

export const FinePrint = styled('p')`
  margin: 12px 0 0 0;

  ${themeFont('tiny')}
`;

interface DressCodeItemProps {
  item: Wws_DressCodeItem;
  className?: string;
  copy: {
    articlesHeading: string;
    affiliatesHeading: string;
    affiliatesIntroBold: string;
    affiliatesLinkText: string;
    affiliatesPostLink: string;
    affiliatesPromoPostLink: string;
    affiliatesFinePrint: string;
    affiliatesImageSrc: string;
    affiliatesImageAlt: string;
    affiliatesLinkHref: string;
  };
}

export const RTR_PROMO_END = new Date('2026-12-31T23:59:59');

export const isRtrPromoActive = (now: Date = new Date()): boolean =>
  now <= RTR_PROMO_END;

export const defaultCopy = {
  articlesHeading: 'Ideas & Advice',
  affiliatesHeading: 'Rent The Runway',
  affiliatesIntroBold: `Why buy attire you’ll only wear once? Rent it.`,
  affiliatesLinkText: 'Rent the Runway',
  affiliatesPostLink:
    'has thousands of premium styles for every RSVP. Save hundreds on your wedding outfit (and a free backup size) along with all your accessories.',
  affiliatesPromoPostLink:
    'has thousands of premium styles for every RSVP. Use code RTRXTHEKNOT to get 25% off all subscription plans or save 20% on one-off rentals over $30.',
  affiliatesFinePrint:
    'The Knot has partnered with third parties to help you navigate and enjoy this event. Purchases made through partner links on this page may earn The Knot a commission.',
  affiliatesImageSrc:
    'https://media-api.xogrp.com/images/3fa85f64-5717-4562-b3fc-2c963f66af29',
  affiliatesImageAlt: 'Woman in red cutout dress in a sunny garden.',
  affiliatesLinkHref:
    'https://renttherunway.pxf.io/0GoBr3',
};

function DressCodeItem({
  item,
  className,
  copy = defaultCopy,
}: DressCodeItemProps) {
  const {
    id,
    description,
    attireDescription,
    showAffiliates,
    showArticles,
    articles,
  } = item;

  const currentRoute = useCurrentRoute();
  const userDecisionArea = 'affiliate link';
  const trackClickingAffiliateLink = useTracking(
    'guest',
    currentRoute,
    userDecisionArea
  );

  const trackClickArticleClick = useTracking('guest', currentRoute);

  // Resolved at render time (not module load) so SSR picks the right copy per request
  const affiliatesPostLink = isRtrPromoActive()
    ? copy.affiliatesPromoPostLink
    : copy.affiliatesPostLink;

  const dangerouslySetInnerHTML = useMemo(
    () => ({
      __html: cleanUserInput(description || ''),
    }),
    [description]
  );

  const renderArticle = (article: Wws_DressCodeArticle | null) => {
    if (!article) return null;

    return (
      <ArticleCard
        key={article.url}
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackClickArticleClick(
            'tk-article-clicked',
            article.title || 'dress code article'
          )
        }
      >
        <FixedNextImage
          src={article.imageUrl || ''}
          alt={article.title}
          width={300}
          height={250}
          sizes="(max-width: 768px) 100vw, 400px"
          style={{ borderRadius: '8px' }}
        />
        <ArticleTitle>{article.title}</ArticleTitle>
      </ArticleCard>
    );
  };

  return (
    <>
      <Item
        className={className}
        data-testid={`${snakeCase('DressCodeItem').replace(/_/g, '-')}-${id}`}
      >
        {attireDescription && (
          <SectionHeading>{attireDescription.toUpperCase()}</SectionHeading>
        )}

        {description && (
          <Description dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        )}

        {showArticles && articles && articles.length > 0 && (
          <ArticlesSection>
            <SectionHeading>{copy.articlesHeading}</SectionHeading>
            {/* Desktop layout: grid of articles */}
            <ArticlesGrid>{articles.map(renderArticle)}</ArticlesGrid>
          </ArticlesSection>
        )}

        {showAffiliates && (
          <AffiliatesSection>
            <SectionHeading>{copy.affiliatesHeading}</SectionHeading>
            <AffiliatesContainer>
              <FixedNextImage
                src={copy.affiliatesImageSrc}
                alt={copy.affiliatesImageAlt}
                width={120}
                height={120}
                sizes="(max-width: 768px) 100vw, 400px"
                style={{ maxWidth: '240px', borderRadius: '14px' }}
              />
              <AffiliatesContent>
                <IntroBold>{copy.affiliatesIntroBold}</IntroBold>
                <br />
                <AffiliateLink
                  href={copy.affiliatesLinkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={copy.affiliatesLinkText}
                  onClick={() => trackClickingAffiliateLink('RTR-link-clicked')}
                >
                  {copy.affiliatesLinkText}
                </AffiliateLink>{' '}
                {affiliatesPostLink}
                <FinePrint>{copy.affiliatesFinePrint}</FinePrint>
              </AffiliatesContent>
            </AffiliatesContainer>
          </AffiliatesSection>
        )}
      </Item>
    </>
  );
}

export default memo(DressCodeItem);
